import React, { useId, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { usePhoneLayout } from "./usePhoneLayout";

type AuthFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "id"> & {
    label: string;
    /** Placeholder used below 900px, where the label is visually hidden. */
    mobilePlaceholder?: string;
    /** Always-visible rule under the field (e.g. the password requirements). */
    hint?: React.ReactNode;
    error?: string | null;
    /** Adds the show/hide toggle. Only for `type="password"`. */
    reveal?: boolean;
    /** Extra class on the wrapping label — the rhythm hooks live here. */
    fieldClassName?: string;
};

/**
 * The auth surface's field primitive (aits-2.0 `.auth-field`): 52px control,
 * label above on desktop, placeholder-only on the phone with the label kept
 * for screen readers, and plain-language validation underneath.
 *
 * Not the AITS-486 `.vb-input` set — that ladder tops out at 42px and the auth
 * spec is deliberately larger. Both share the focus-ring token.
 */
export default function AuthField({
    label,
    mobilePlaceholder,
    hint,
    error,
    reveal = false,
    fieldClassName = "",
    placeholder,
    type = "text",
    ...inputProps
}: AuthFieldProps) {
    const reactId = useId();
    const inputId = `auth-${reactId}`;
    const errorId = `${inputId}-error`;
    const hintId = `${inputId}-hint`;
    const isPhone = usePhoneLayout();
    const [revealed, setRevealed] = useState(false);

    const resolvedPlaceholder =
        isPhone && mobilePlaceholder ? mobilePlaceholder : placeholder;
    const resolvedType = reveal && revealed ? "text" : type;

    const describedBy = [hint ? hintId : null, error ? errorId : null]
        .filter(Boolean)
        .join(" ");

    const input = (
        <input
            {...inputProps}
            id={inputId}
            type={resolvedType}
            placeholder={resolvedPlaceholder}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy || undefined}
        />
    );

    return (
        <label className={`auth-field ${fieldClassName}`.trim()} htmlFor={inputId}>
            <span className="auth-label-text">{label}</span>

            {reveal ? (
                <span className="auth-field__wrap">
                    {input}
                    <button
                        type="button"
                        className="auth-field__reveal"
                        onClick={() => setRevealed((v) => !v)}
                        aria-label={revealed ? "Hide password" : "Show password"}
                    >
                        {revealed ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </span>
            ) : (
                input
            )}

            {hint ? (
                <span className="auth-hint" id={hintId}>
                    {hint}
                </span>
            ) : null}

            {/* Always mounted, visibility toggled by class: a live region that is
                inserted at the same moment as its text is announced unreliably. */}
            <span
                className={error ? "auth-error auth-error--show" : "auth-error"}
                id={errorId}
                role="alert"
            >
                {error ?? ""}
            </span>
        </label>
    );
}
